// Maps backend ai:stage strings → friendly UI copy.
// Keys must be the literal strings emitted by server/ai2/loop*.js
// (the STAGE constant near the top of each loop file). Don't rename
// without updating the loop in lockstep.
//
// Each stage has:
//   primaryPool     — 2-3 alternatives for the headline label.
//                     One drawn at random when the stage starts.
//   subtextPools[i] — pool of 5-6 (fewer for brief stages) entries
//                     for cycle position i (0=3s, 1=6.5s, 2=10s).
//                     One drawn at random as the cycle advances.
//
// Why pools: a single conversation often runs 3-5 iterations of the
// agent loop, each emitting the same stage strings. Fixed copy makes
// the system feel monotonous; random draws from semantically-
// equivalent pools keep the UI feeling fresh.

// Pure helper — selection happens at the consumer (PendingIndicator)
// at the moment a stage starts or the cycle advances, so each draw
// is fresh.
function pickRandom(pool) {
  if (!Array.isArray(pool) || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

const STAGE_CONFIG = {
  "Loading tenant context…": {
    primaryPool: [
      "Loading your workspace",
      "Loading workspace settings",
      "Pulling tenant context",
    ],
    subtextPools: [
      [
        "Pulling your tenant settings and permissions",
        "Fetching the org configuration",
        "Reading the tenant profile",
      ],
      [
        "Loading the org hierarchy",
        "Reading enabled module flags",
        "Stitching the workspace context together",
      ],
      [
        "Almost ready to start",
        "Just finishing the workspace load",
        "Wrapping up the tenant fetch",
      ],
    ],
  },

  "Preparing knowledge pack…": {
    primaryPool: [
      "Loading database knowledge",
      "Preparing the model's context",
      "Briefing the model",
    ],
    subtextPools: [
      [
        "Reading the schema and column aliases",
        "Loading the table catalog",
        "Stitching the knowledge pack together",
      ],
      [
        "Adding tenant-specific terms to the brief",
        "Wiring up the tool definitions",
        "Compiling the system prompt",
      ],
      [
        "Almost ready to send to the model",
        "Just finishing the briefing",
        "Wrapping up the context",
      ],
    ],
  },

  "Connecting to AI…": {
    primaryPool: [
      "Sending your question",
      "Passing the question along",
      "Handing it off",
    ],
    subtextPools: [
      [
        "The question is on its way",
        "Pushing the prompt through",
        "The request is in flight",
      ],
      [
        "The handoff is taking a moment",
        "A little slower than usual today",
        "Still on its way",
      ],
      [
        "Bigger prompts can take a moment to land",
        "Taking longer than expected",
        "Almost ready to start thinking",
      ],
    ],
  },

  "Reasoning…": {
    primaryPool: [
      "Thinking through the question",
      "Working through your question",
      "Reasoning about the answer",
    ],
    subtextPools: [
      [
        "Looking at the right tables and filters",
        "Working out which columns to query",
        "Picking the right filters",
        "Mapping the question to data",
        "Choosing the joins",
        "Tracing through the schema",
      ],
      [
        "The model thinks before producing tokens",
        "Reasoning through the question",
        "Working out the right approach",
        "Considering how to answer",
        "Mapping out the steps",
        "Putting together a plan",
      ],
      [
        "Composing the response now",
        "Almost ready to answer",
        "Finalizing the approach",
        "Putting the answer together",
        "Wrapping up the reasoning",
        "Writing the response",
      ],
    ],
  },

  "Receiving response…": {
    primaryPool: [
      "Composing the answer",
      "Reading the response",
      "Streaming the answer",
    ],
    subtextPools: [
      [
        "Streaming tokens from the model",
        "Reading the response as it arrives",
        "Catching the model's output",
        "Receiving the answer",
      ],
      [
        "Putting the final touches in place",
        "Almost done with the response",
        "Wrapping up the answer",
      ],
      [
        "Just finishing the response",
        "Almost there",
        "Wrapping up",
      ],
    ],
  },

  "Running tools…": {
    primaryPool: [
      "Running the query",
      "Executing the tool",
      "Pulling the data",
    ],
    subtextPools: [
      [
        "Executing against your live data",
        "Running the SQL",
        "Pulling rows from the database",
        "Asking the database for the numbers",
      ],
      [
        "Larger datasets take a moment",
        "The database is processing",
        "Aggregating across many rows",
        "The query touches a lot of data",
      ],
      [
        "Almost done with the query",
        "Wrapping up the database call",
        "Just finishing the lookup",
        "Pulling the last rows together",
      ],
    ],
  },

  "Continuing…": {
    primaryPool: [
      "Looking at the results",
      "Reviewing what came back",
      "Picking up where it left off",
    ],
    subtextPools: [
      [
        "Reviewing the data that came back",
        "Reading the previous step's output",
        "Looking through the results",
        "Examining what the database returned",
        "Skimming the result set",
      ],
      [
        "Working out what to do next",
        "Deciding on the next step",
        "Considering whether to drill deeper",
        "Planning the next move",
        "Thinking about how to present this",
      ],
      [
        "Almost ready to continue",
        "Finalizing the next step",
        "Just lining things up",
        "Wrapping up the review",
      ],
    ],
  },
};

// Safety net for any future stage the dictionary doesn't cover yet.
// Every stage the backend currently emits is mapped above, so these
// shouldn't render in normal flow.
const FALLBACK_PRIMARY_POOL = [
  "Thinking",
  "Working through it",
  "Processing",
];

const FALLBACK_SUBTEXT_POOLS = [
  [
    "Working through your request",
    "Processing the step",
    "Handling the work in progress",
  ],
  [
    "Some steps take a moment",
    "Still working through it",
    "The step is taking a little longer",
  ],
  [
    "Almost there",
    "Wrapping up",
    "Just about done",
  ],
];

// ─── Public selectors ──────────────────────────────────────────────
// PendingIndicator calls these once per stage transition (selectPrimary)
// or once per cycle advance (selectSubtext). Each call draws fresh, so
// repeated transitions through the same stage produce different copy.

export function selectPrimary(stage) {
  const config = STAGE_CONFIG[stage];
  if (config) return pickRandom(config.primaryPool);
  // Unmapped stage — strip trailing ellipsis from the literal string
  // (e.g. "Some New Stage…" → "Some New Stage"). If the stage is
  // missing entirely, fall through to the fallback pool.
  if (stage) return stage.replace(/[…\.]+\s*$/u, "");
  return pickRandom(FALLBACK_PRIMARY_POOL);
}

export function selectSubtext(stage, cycleIdx) {
  if (cycleIdx < 0) return null;
  const config = STAGE_CONFIG[stage];
  const pools = (config && config.subtextPools) || FALLBACK_SUBTEXT_POOLS;
  if (cycleIdx >= pools.length) return null;
  return pickRandom(pools[cycleIdx]);
}

export function subtextCycleCount(stage) {
  const config = STAGE_CONFIG[stage];
  const pools = (config && config.subtextPools) || FALLBACK_SUBTEXT_POOLS;
  return pools.length;
}
